import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// mui
import { Box, Typography, Card, CardContent, Avatar, Stack, Skeleton } from "@mui/material";

// assets
import pmdriveinlogo from "../../assets/pmdrivein.PNG";

// components
import TopBar from "../components/TopBar";

// api utils
import { callApi, ip } from "../../utils/api.helpers";

export default function MenuPage() {
  const location = useLocation();
  const restaurant = location.state?.restaurant;

  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryImages, setCategoryImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState(true);

  const sectionRefs = useRef({});
  const containerRef = useRef();
  const categoriesRef = useRef();
  const scrollTimeoutRef = useRef();

  async function fetchMenuData() {
    setLoading(true);
    try {
      let url = `${ip}/api/restaurants/${restaurant?._id}/menu`;
      let result = await callApi(url, "GET");
      setMenuData(result.data);
      if (result.data?.length) {
        setActiveCategory(result.data[0]._id);
      }
    } catch (e) {
      alert(e?.message);
    } finally {
      setLoading(false);
    }
  }

  const handleCategoryClick = (id) => {
    setActiveCategory(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });

    // Clear any pending scroll timeouts
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Temporarily disable scroll detection to prevent interference
    scrollTimeoutRef.current = setTimeout(() => {
      scrollTimeoutRef.current = null;
    }, 1000);
  };

  const scrollToCategory = (categoryId) => {
    if (!categoriesRef.current) return;

    const categoryElements = categoriesRef.current.children;
    const activeIndex = menuData.findIndex((cat) => cat._id === categoryId);

    if (activeIndex >= 0 && categoryElements[activeIndex]) {
      const categoryElement = categoryElements[activeIndex];
      const container = categoriesRef.current;
      const containerWidth = container.clientWidth;
      const categoryLeft = categoryElement.offsetLeft;
      const categoryWidth = categoryElement.clientWidth;

      // Calculate scroll position to center the category
      const scrollTo = categoryLeft - containerWidth / 2 + categoryWidth / 2;

      container.scrollTo({
        left: scrollTo,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    // Skip scroll handling if we're in the middle of a programmatic scroll
    if (scrollTimeoutRef.current || !menuData) return;

    const scrollPosition = containerRef.current.scrollTop;
    let currentActive = activeCategory;

    // Find which category is currently at the top of the viewport
    for (let i = 0; i < menuData.length; i++) {
      const cat = menuData[i];
      const element = sectionRefs.current[cat._id];

      if (element) {
        const elementTop = element.offsetTop - containerRef.current.offsetTop - 80;
        const elementBottom = elementTop + element.offsetHeight;

        // Check if element is in view
        if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
          currentActive = cat._id;
          break;
        }
      }
    }

    if (currentActive !== activeCategory) {
      setActiveCategory(currentActive);
      scrollToCategory(currentActive);
    }
  };

  const formatImageName = (menuCategoryName) => {
    return menuCategoryName?.replace(/\s+/g, "").toLowerCase();
  };

  const loadCategoryImages = async () => {
    if (!menuData || !restaurant?._id) return;

    setImagesLoading(true);
    try {
      const images = {};
      for (let i = 0; i < menuData.length; i++) {
        try {
          // Dynamically import the image
          const imageModule = await import(`../../assets/${restaurant._id}/${formatImageName(menuData[i]?._id)}.png`);
          images[menuData[i]._id] = imageModule.default;
        } catch (error) {
          images[menuData[i]._id] = pmdriveinlogo;
        }
      }
      setCategoryImages(images);
    } catch (e) {
      alert(e?.message);
    } finally {
      setImagesLoading(false);
    }
  };

  useEffect(() => {
    if (menuData) {
      loadCategoryImages();
    }
  }, [menuData, restaurant?._id]);

  useEffect(() => {
    fetchMenuData();

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <TopBar />

      {/* Sticky Category Bar with Fixed Height */}
      <Box
        ref={categoriesRef}
        sx={{
          position: "sticky",
          top: 20,
          zIndex: 10,
          bgcolor: "white",
          px: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          overflowX: "auto",
          display: "flex",
          gap: 2,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          height: 90,
        }}
      >
        {menuData?.map((cat, index) => {
          return (
            <Stack
              key={index}
              alignItems="center"
              sx={{
                cursor: "pointer",
                flex: "0 0 70px",
                textAlign: "center",
                whiteSpace: "normal",
                wordBreak: "break-word",
                borderBottom: activeCategory === cat._id ? "2px solid black" : "none",
                pb: 0.5,
                height: "100%",
                justifyContent: "space-between",
              }}
              onClick={() => handleCategoryClick(cat._id)}
            >
              {imagesLoading ? (
                <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
              ) : (
                <Avatar
                  src={categoryImages[cat._id] || pmdriveinlogo}
                  alt={cat._id}
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                  }}
                />
              )}

              <Typography
                variant="body2"
                sx={{
                  fontWeight: activeCategory === cat._id ? 600 : 500,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  lineHeight: 1.2,
                  fontSize: "0.75rem",
                }}
              >
                {cat._id}
              </Typography>
            </Stack>
          );
        })}
      </Box>

      {/* Menu Items */}
      <Box ref={containerRef} sx={{ p: 2 }} onScroll={handleScroll} style={{ height: "calc(100vh - 20vh)", overflowY: "auto" }}>
        {loading ? (
          [1, 1, 1, 1, 1].map((el) => {
            return (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={200}
                sx={{
                  borderRadius: 2,
                  marginTop: "10px",
                  marginBottom: "10px",
                  boxShadow: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              />
            );
          })
        ) : menuData?.length ? (
          menuData?.map((cat, index) => (
            <Box key={index} ref={(el) => (sectionRefs.current[cat?._id] = el)} sx={{ mb: 4 }} id={`section-${cat._id}`}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: "text.primary" }}>
                {cat?._id.toUpperCase()}
              </Typography>

              {cat?.data?.map((item, idx) => (
                <Card
                  key={idx}
                  sx={{
                    mb: 1.5,
                    boxShadow: 2,
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {item?.item}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      ₹{item?.amount}
                    </Typography>

                    {item?.quantity && (
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.quantity}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ))
        ) : (
          <Box>No Menu data to show</Box>
        )}
      </Box>
    </Box>
  );
}
